import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { createApiClient } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

// #299: Der Bedien-Kontext des angemeldeten Menschen. Die UI kennt nur VORGANGS-NAMEN;
// welche Permission dahinter steht (inkl. ODER-Regeln wie `git-content:write |
// plugins:author` aus #295) entscheidet allein der Server — dieselbe Tabelle, die auch
// das Gate stellt. Damit kann die Anzeige nicht mehr von der Durchsetzung abweichen.
//
// ⚠ Das hier ist BEQUEMLICHKEIT, keine Sicherheit: der Server prüft jede Aktion
// unabhängig weiter. Wer das DOM manipuliert, gewinnt genau nichts.
export interface UserContext {
  userId: string;
  role: string;
  capabilities: string[];
  /** Vorgänge, die DIESER Mensch ausführen darf. */
  operations: string[];
  /** Vorgänge, die DIESER Server überhaupt kennt (Version-Skew, optional). */
  knownOperations?: string[];
}

export function useUserContext() {
  const { session, logout } = useAuth();
  return useQuery<UserContext>({
    queryKey: ["user-context", session?.user.id ?? "anon"],
    queryFn: () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      return client.get<UserContext>("/users/me/context");
    },
    enabled: !!session,
    // #299 (Review L-3): kurz genug, dass ein entzogenes Recht zeitnah verschwindet —
    // die Sicherheit haengt ohnehin nicht daran (der Server entscheidet bei jeder Aktion).
    staleTime: 60_000,
  });
}

/**
 * #299: Darf dieser Mensch den Vorgang — aus Sicht der ANZEIGE.
 *
 * Drei Fälle, und der dritte ist der Grund, warum der Server `knownOperations` mitschickt:
 *  1. Kontext noch nicht geladen → anzeigen (kein Flackern, kein kurzzeitiges Verstecken).
 *  2. Server kennt den Vorgang, hat ihn aber nicht erlaubt → verstecken.
 *  3. Server kennt den Vorgang GAR NICHT (ältere Version) → anzeigen.
 *
 * Fall 3 ist bewusst gegen unsere Default-closed-Gewohnheit — die gilt für
 * Sicherheitsentscheidungen, hier geht es um Darstellung. Falsch verstecken macht ein
 * erlaubtes Feature unsichtbar (der Mensch sucht und findet es nie); falsch zeigen endet
 * in einer klaren Server-Fehlermeldung. Nur eine dieser Richtungen ist gutartig.
 */
const gemeldet = new Set<string>();

export function darfVorgang(ctx: UserContext | undefined, op: string): boolean {
  // ⚠ Robustheit vor Eleganz: hier lief ein `ctx.operations.includes(...)` auf einer
  // Antwort, die kein Kontext-Objekt war — der Zugriff warf, und React riss die GANZE
  // Seite ab (im Test: leerer <body>). Eine Anzeige-Hilfe darf niemals die Oberfläche
  // fällen; alles Unerwartete führt deshalb zur gutartigen Richtung „anzeigen".
  const erlaubt = Array.isArray(ctx?.operations) ? ctx.operations : null;
  if (!erlaubt) return true;
  if (erlaubt.includes(op)) return true;
  // Ohne `knownOperations` (älterer Master) lässt sich „unbekannt" nicht von „verboten"
  // unterscheiden → dieselbe gutartige Richtung wählen wie in Fall 3.
  const bekannt = Array.isArray(ctx?.knownOperations) ? ctx.knownOperations : null;
  if (!bekannt) return true;
  if (bekannt.includes(op)) return false; // bekannt, aber nicht erlaubt
  // Unbekannt = dieser Server kennt den Vorgang nicht. Einmal melden, damit ein Tippfehler
  // im `op`-Attribut nicht fuer immer unsichtbar bleibt (Review L-4).
  if (!gemeldet.has(op)) {
    gemeldet.add(op);
    console.warn(`[#299] Unbekannter Vorgang "${op}" — der Server kennt ihn nicht. Element wird angezeigt; der Server entscheidet.`);
  }
  return true;
}

/**
 * #299 (Review R2 L-D/L-E): dieselbe Frage, aber mit dem LADEZUSTAND — für Stellen, an
 * denen kurzes Flackern schadet (destruktive Schalter) oder ein Abruf auf Verdacht Geld
 * kostet (403er in der Sidebar).
 *
 *  - lädt noch  → `false` (nichts anbieten, nichts abrufen — es dauert einen Wimpernschlag)
 *  - geladen    → die normale Entscheidung
 *  - **fehlgeschlagen** → `true`
 *
 * Der letzte Fall ist der wichtige: ohne ihn würde ein Master ohne diesen Endpunkt (oder
 * ein Netzfehler) einen ADMIN dauerhaft aussperren — die Anzeige-Hilfe hätte dann mehr
 * kaputtgemacht als sie nützt. Der Server entscheidet ohnehin.
 */
export function darfJetzt(
  ctx: { isLoading: boolean; isSuccess: boolean; data?: UserContext },
  op: string,
): boolean {
  if (ctx.isLoading) return false;
  if (ctx.isSuccess) return darfVorgang(ctx.data, op);
  return true;
}

/**
 * `<Wenn op="plugin.create">…</Wenn>` — rendert die Kinder nur, wenn der Server diesen
 * Vorgang für den angemeldeten Menschen erlaubt. `sonst` für einen Ersatz-Inhalt.
 */
export function Wenn({ op, children, sonst = null }: { op: string; children: ReactNode; sonst?: ReactNode }) {
  const { data } = useUserContext();
  return <>{darfVorgang(data, op) ? children : sonst}</>;
}
